/**
 * The user library — §4.1, §13.1.
 *
 * Write-through to IndexedDB, MIRRORED IN MEMORY so components render synchronously.
 * Owner of: notes, bookmarks, page read-state.
 */
import { create } from 'zustand'
import { dayStamp, type Bookmark, type Note, type PageState } from '@mushaf/corpus'
import { put, readSnapshot, remove, replaceAll, type LibrarySnapshot } from '../lib/db'

interface LibraryState {
  hydrated: boolean
  notes: Record<string, Note>
  bookmarks: Record<string, Bookmark>
  pages: Record<number, PageState>

  hydrate: () => Promise<void>
  replaceFromSnapshot: (snapshot: LibrarySnapshot) => Promise<void>
  snapshot: () => LibrarySnapshot

  setNote: (verseKey: string, text: string) => Promise<void>
  toggleBookmark: (verseKey: string) => Promise<void>
  markRead: (page: number, now: number) => Promise<void>
}

const byKey = <T, K extends string | number>(list: T[], key: (t: T) => K): Record<K, T> =>
  Object.fromEntries(list.map((t) => [key(t), t])) as Record<K, T>

export const useLibrary = create<LibraryState>()((set, get) => ({
  hydrated: false,
  notes: {},
  bookmarks: {},
  pages: {},

  hydrate: async () => {
    const s = await readSnapshot()
    set({
      hydrated: true,
      notes: byKey(s.notes, (n) => n.verseKey),
      bookmarks: byKey(s.bookmarks, (b) => b.verseKey),
      pages: byKey(s.pages, (p) => p.page),
    })
  },

  replaceFromSnapshot: async (snapshot) => {
    await replaceAll(snapshot)
    await get().hydrate()
  },

  snapshot: () => {
    const s = get()
    return {
      notes: Object.values(s.notes),
      bookmarks: Object.values(s.bookmarks),
      pages: Object.values(s.pages),
    }
  },

  setNote: async (verseKey, text) => {
    const trimmed = text.trim()
    // AN EMPTIED NOTE IS A DELETED NOTE, or the list fills with blank entries the
    // reader cannot see or remove (§8).
    if (!trimmed) {
      const notes = { ...get().notes }
      delete notes[verseKey]
      set({ notes })
      await remove('notes', verseKey)
      return
    }
    const note: Note = { verseKey, text: trimmed, updatedAt: Date.now() }
    set({ notes: { ...get().notes, [verseKey]: note } })
    await put('notes', note)
  },

  toggleBookmark: async (verseKey) => {
    const bookmarks = { ...get().bookmarks }
    if (bookmarks[verseKey]) {
      delete bookmarks[verseKey]
      set({ bookmarks })
      await remove('bookmarks', verseKey)
      return
    }
    const bookmark: Bookmark = { verseKey, createdAt: Date.now() }
    set({ bookmarks: { ...bookmarks, [verseKey]: bookmark } })
    await put('bookmarks', bookmark)
  },

  markRead: async (page, now) => {
    const today = dayStamp(now)
    const existing = get().pages[page]
    // Must NOT rewrite readOn if the page was already read today — it makes no
    // difference to anything and churns the store on every page turn (§13.1).
    if (existing?.read && existing.readOn === today) return
    const next: PageState = { page, read: true, readOn: today }
    set({ pages: { ...get().pages, [page]: next } })
    await put('pages', next)
  },
}))
